import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AI_MODES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { LightningIcon, TelevisionIcon, ArrowRightIcon, CaretDownIcon, XIcon } from "@phosphor-icons/react";
import TeamLogo from "@/components/TeamLogo";
import WorldSeriesBadge from "@/components/WorldSeriesBadge";

function StatusBadge({ status, period }) {
  if (status === "LIVE") {
    return (
      <span data-testid="status-live" className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#FF0000]/15 text-[#FF453A] border border-[#FF453A]/40">
        <span className="live-dot" />
        <span className="text-[10px] font-black uppercase tracking-[0.15em]">{period || "Live"}</span>
      </span>
    );
  }
  if (status === "FINAL") {
    return (
      <span data-testid="status-final" className="inline-flex items-center px-2 py-1 rounded bg-white/10 border border-white/15">
        <span className="text-[10px] font-black uppercase tracking-[0.15em] text-white/80">Final</span>
      </span>
    );
  }
  return (
    <span data-testid="status-upcoming" className="inline-flex items-center px-2 py-1 rounded bg-[#0A84FF]/15 border border-[#0A84FF]/40">
      <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[#0A84FF]">{period || "Upcoming"}</span>
    </span>
  );
}

function TeamRow({ abbr, name, score, color, logo, winning }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-3 min-w-0">
        <TeamLogo abbr={abbr} logo={logo} color={color} size={36} />
        <div className="font-display font-bold uppercase text-sm tracking-tight truncate text-white/80">{name}</div>
      </div>
      <div className={`font-num text-3xl font-black ${winning ? "text-white" : "text-white/60"}`}>{score}</div>
    </div>
  );
}

export default function GameCard({ game }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(null);
  const [output, setOutput] = useState(null);

  const language = user?.language_preference || "en";
  const homeWinning = game.home_score > game.away_score && game.status !== "UPCOMING";
  const awayWinning = game.away_score > game.home_score && game.status !== "UPCOMING";

  // Memo: extra AI modes shown in the dropdown panel (everything except the primary "why_it_matters")
  const extraModes = useMemo(() => AI_MODES.filter((m) => m.key !== "why_it_matters"), []);

  const runMode = async (modeKey) => {
    setLoading(modeKey);
    setOutput(null);
    setOpen(false);
    try {
      const endpoint =
        modeKey === "why_it_matters"
          ? `/games/${game.id}/why-it-matters`
          : `/games/${game.id}/ai-mode`;
      const body = modeKey === "why_it_matters" ? { language } : { mode: modeKey, language };
      const res = await api.post(endpoint, body);
      const label = AI_MODES.find((m) => m.key === modeKey)?.label || "Gamly AI";
      setOutput({ mode: modeKey, label, text: res.data.text });
    } catch {
      setOutput({ mode: modeKey, label: "Error", text: "Could not generate. Try again." });
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="bg-[#121212] border border-white/10 rounded-md overflow-hidden hover:border-white/25 transition-colors" data-testid={`game-card-${game.id}`}>
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">{game.league}</span>
          <WorldSeriesBadge game={game} compact />
          {game.is_demo && (
            <span data-testid="demo-badge" className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#FDB927]/15 border border-[#FDB927]/45 text-[#FDB927] text-[9px] font-black uppercase tracking-[0.15em]">Demo</span>
          )}
          {game.tv && (
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
              <TelevisionIcon size={11} /> {game.tv}
            </span>
          )}
        </div>
        <StatusBadge status={game.status} period={game.period_label} />
      </div>
      <div className="px-4 py-3">
        <TeamRow abbr={game.away_team_abbr} name={game.away_team} score={game.away_score} color={game.away_team_color} logo={game.away_team_logo} winning={awayWinning} />
        <TeamRow abbr={game.home_team_abbr} name={game.home_team} score={game.home_score} color={game.home_team_color} logo={game.home_team_logo} winning={homeWinning} />

        {game.status !== "UPCOMING" && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1">
              <span>Win Prob</span>
              <span>{game.away_team_abbr} {game.win_probability_away}% · {game.home_team_abbr} {game.win_probability_home}%</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden bg-white/5 flex">
              <div className="h-full" style={{ width: `${game.win_probability_away}%`, backgroundColor: game.away_team_color }} />
              <div className="h-full" style={{ width: `${game.win_probability_home}%`, backgroundColor: game.home_team_color }} />
            </div>
          </div>
        )}

        <div className="mt-3 text-[11px] text-white/50">
          <span className="font-bold uppercase tracking-wider text-white/40">Key matchup · </span>
          <span className="text-white/80 font-medium">{game.key_matchup}</span>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-2">
        <div className="flex gap-2">
          <button
            onClick={() => runMode("why_it_matters")}
            disabled={!!loading}
            data-testid={`why-it-matters-${game.id}`}
            className="wim-btn flex-1 py-3 rounded-md bg-white/5 border border-[#0A84FF]/60 text-[#0A84FF] hover:bg-[#0A84FF] hover:text-white font-black text-base uppercase tracking-wide transition-colors duration-200 inline-flex items-center justify-center gap-2"
          >
            <LightningIcon weight="fill" /> {loading === "why_it_matters" ? "Thinking…" : "Why It Matters"}
          </button>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid={`ai-modes-${game.id}`}
            className="px-3 py-3 rounded-md border border-white/15 text-white/80 hover:text-white hover:bg-white/5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider"
          >
            Modes <CaretDownIcon size={12} />
          </button>
        </div>
        <Link
          to={`/games/${game.id}`}
          data-testid={`open-live-${game.id}`}
          className={`block w-full py-2 rounded-md font-bold text-xs uppercase tracking-wider text-center transition-colors ${
            game.is_demo
              ? "bg-[#FDB927]/15 border border-[#FDB927]/50 text-[#FDB927] hover:bg-[#FDB927] hover:text-black"
              : "border border-white/10 text-white/70 hover:text-white hover:border-white/25"
          }`}
        >
          {game.is_demo ? "Open Live Pitch View" : "Open Live Companion"} <ArrowRightIcon size={12} className="inline" />
        </Link>
      </div>

      {/* AI Modes panel */}
      {open && (
        <div className="border-t border-white/10 px-4 py-3 bg-black/40" data-testid={`ai-modes-panel-${game.id}`}>
          <div className="grid grid-cols-2 gap-2">
            {extraModes.map((m) => (
              <button
                key={m.key}
                disabled={!!loading}
                onClick={() => runMode(m.key)}
                data-testid={`mode-${m.key}-${game.id}`}
                className="text-left p-2.5 rounded-md border border-white/10 bg-white/[0.02] hover:border-[#0A84FF]/50 hover:bg-[#0A84FF]/5 transition-colors"
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <m.icon weight="bold" size={12} className="text-[#0A84FF]" />
                  <span className="font-display font-bold text-xs uppercase tracking-tight">{m.label}</span>
                </div>
                <p className="text-[10px] text-white/45 line-clamp-1">{m.desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="border-t border-white/10 px-4 py-3 bg-black/40">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A84FF] mb-1">Gamly Thinking · {AI_MODES.find((m) => m.key === loading)?.label}</p>
          <div className="h-1 bg-white/5 rounded overflow-hidden">
            <div className="h-full bg-[#0A84FF] animate-pulse" style={{ width: "60%" }} />
          </div>
        </div>
      )}

      {output && (
        <div className="border-t border-white/10 px-4 py-3 bg-black/40" data-testid={`why-it-matters-output-${game.id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#32D74B]">{output.label}</p>
            <button onClick={() => setOutput(null)} className="text-white/40 hover:text-white" data-testid={`close-output-${game.id}`}><XIcon size={14} /></button>
          </div>
          <pre className="whitespace-pre-wrap font-medium text-sm text-white/90 leading-relaxed font-sans">{output.text}</pre>
        </div>
      )}
    </div>
  );
}
